import { defineAsyncComponent, reactive } from "vue";

import store from "../../../../../store/index";
import i18n from "../../../../../i18n";
import { createLoading } from "../../../../../utils/loading";
import { required } from "../../../../../utils/validate";
import {
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../../utils/page";

import {
    onClickActiveAll,
    onClickArchiveAll,
    onClickCreate,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from "../../../../../utils/form";

import {
    createColumnBoolean,
    createColumnInput,
    createColumnInputNumber,
    createColumnSelect
} from '../../../../../components/table/c-table.logic';
import {
    getLabType,
    getLabTypeByKey,
    getLabTypeKeyByRoleMenuAttributes,
    strConvertToUnderScore
} from "../lab-share.logic";
import { addModal } from "../../../../../app.logic";
import lodash from "lodash";
import { modalStyleIFrame } from "../../../../../utils/constant";
import { has } from "../../../../../utils/menu-context";

export const { isLoading, setIsLoading } = createLoading();
export const actionPrefix = "resource/v1/master/labTopic";
export const routerPrefix = "topics";
export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, {
    ids,
    config: { params: { isHardDelete: true } }
});
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const getRepeatOption = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", {
    group: "LAB_REPEAT_OPTION",
    value: searchQuery
});

const CIFrame = defineAsyncComponent(() => import('../../../../../components/iframe/CIFrame'))

export const openModalList = ({ bind, onIframeMounted, disabledSearch, isForceDisabledCreate, isForceInvisibleSearch }) => {
    let disableParam = ''
    let isForceDisabledCreateParam = ''
    let isForceInvisibleSearchParam = ''
    if (disabledSearch?.length) {
        disabledSearch?.forEach(it => disableParam += '&disabledSearch='+it);
    }
    if (isForceDisabledCreate) {
        isForceDisabledCreateParam = '&isForceDisabledCreate=true'
    }
    if (isForceInvisibleSearch) {
        isForceInvisibleSearchParam = '&isForceInvisibleSearch=true'
    }
    const options = addModal({
            bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
            render: () => <CIFrame src={`${process.env.VUE_APP_PUBLIC_PATH}/app/master-mgt/lab/topics?isLayout=false${isForceDisabledCreate ? isForceDisabledCreateParam: ''}${isForceInvisibleSearch ? isForceInvisibleSearchParam: ''}&canSelect=true&cacheQuery=true${disableParam.length ? disableParam: ''}`} onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}></CIFrame>
    })
    return options
}

export const getLabFormulaByGroup = ({ searchQuery, group }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", {
    group: group,
    value: searchQuery
});

export const createColumns = ({ props, data }) => {
    const isLabPhysic = data.labAttributes?.length === 1 && data.labAttributes[0] === "labPhysic"
    let columns =  [
        createColumnInput({
            title: () => i18n.global.t("model.topic.name"), dataIndex: "name", width: 150, align: "center"
        }),
        createColumnInput({
            visible: () => !isLabPhysic,
            title: () => i18n.global.t("model.topic.nameS"), dataIndex: "nameS", width: 150, align: "center",
            search: { visible: () => !isLabPhysic },
        }),
        createColumnInput({
            visible: () => !isLabPhysic,
            width: 80,
            title: () => i18n.global.t("model.topic.refLabTopicUnit"),
            dataIndex: "refLabTopicUnit.value",
            search: { visible: () => !isLabPhysic },
            align: "center"
        }),
        createColumnSelect({
            visible: () => true,
            width: 80,
            title: () => i18n.global.t("model.lookup.modelGroup.LAB_FORMULA"),
            dataIndex: "refLabFormula.value",
            inputProp: ({ field }) => {
                const group = strConvertToUnderScore({ input: { data: data.searchLabType?.key || '', transform: { suffix: '_FORMULA' }}});
                return {
                    disabled: !data.searchLabType,
                    list: ({ searchQuery }) => getLabFormulaByGroup({ searchQuery, group }),
                    optLabel: "value",
                    'onUpdate:value': (value) => {
                        field.searchValue = value
                    }
                }
            },
            search: { name: "refLabFormula.id" },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.topic.refRepeatOption"),
            width: 80,
            dataIndex: "refRepeatOption.value",
            inputProp: () => ({ list: getRepeatOption, optLabel: "value" }),
            search: { name: "refRepeatOption.id" },
            align: "center"
        }),
        createColumnBoolean({
            visible: () => isLabPhysic,
            title: () => i18n.global.t("model.topic.isCanChangeRepeatOption"),
            dataIndex: "isCanChangeRepeatOption",
            sorter: false,
            search: { visible: () => isLabPhysic },
        }),
        createColumnInputNumber({
            visible: () => !isLabPhysic,
            title: () => i18n.global.t("model.topic.sub"),
            width: 40,
            dataIndex: "sub",
            comma: true,
            inputProp: () => ({ comma: true }),
            search: { visible: () => !isLabPhysic },
            align: "center"
        }),
        createColumnInputNumber({
            title: () => i18n.global.t("model.topic.seq"), dataIndex: "seq", comma: true,
            inputProp: () => ({ comma: true }),
            align: "center"
        }),
    ];
    if (getLabTypeKeyByRoleMenuAttributes({ props }).length > 1) {
        columns.unshift(createColumnSelect({
            title: () => i18n.global.t("model.lookup.modelGroup.LAB_TYPE"),
            dataIndex: "refLabType.value",
            inputProp: ({ field }) => {
                const formulaField = field.columns.find(col => col.dataIndex == 'refLabFormula.id').search.field;
                return {
                    visible: () => data.labAttributes.length == 1,
                    list: ({ searchQuery }) => getLabType({ searchQuery }).then(arrays => {
                        return arrays.filter(item => data.labAttributes.includes(item?.key));
                    }),
                    'onUpdate:value': (value) => {
                        field.searchValue = value;
                        data.searchLabType = value;
                        formulaField.searchValue = null
                    },
                    optLabel: 'value'
                }
            },
            search: {
                name: "refLabType.id",
                field: {
                    searchValue: data.searchLabType,
                    rulesRef: [required],
                    visible: () => getLabTypeKeyByRoleMenuAttributes({ props }).length > 1
                }
            }
        }))
    }

    return columns
};

export const initState = ({ props, data }) => {
    const menuContext = props.menuContext;
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];
    getLabTypeByKey({searchQuery: data.labAttributes[0]}).then(res => {
        data.searchLabType = res[0]
        if (state.columns.length == 0) {
            state.columns = createColumns({props, data});
        }
        state.manages = [
            createManageSelect({ menuContext }),
            createManageActive({
                menuContext,
                onClick: ({ rowSelection }) => onClickActiveAll({
                    rowSelection,
                    callActiveAll,
                    state,
                    onSearch: (search) => onSearch({...search, props}),
                    setIsLoading
                })
            }),
            createManageArchive({
                menuContext,
                onClick: ({ rowSelection }) => onClickArchiveAll({
                    rowSelection,
                    callArchiveAll,
                    state,
                    onSearch: (search) => onSearch({...search, props}),
                    setIsLoading
                })
            }),
            createManageDelete({
                menuContext,
                onClick: ({ rowSelection }) => onClickDeleteAll({
                    rowSelection,
                    callDeleteAll,
                    state,
                    onSearch: (search) => onSearch({...search, props}),
                    setIsLoading
                })
            }),
            createManageExportCsv({ menuContext })
        ];
        state.manageExtras = [createManageExtraCreate({
            menuContext,
            onClick: () => onClickCreate({ routerPrefix }),
            visible: () => has({ menuContext: menuContext, key: "canCreate" }) && !data.isForceDisabledCreate,
        })];
        state.rowActions = [
            createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
            createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) })
        ];
    });
};

export const onSearch = ({ page, props }) => {
    setIsLoading(true);
    const where = page.where || [];
    const whereLabType = [{ name: 'refParent.id', operator: "=", type:"UUID", value: null }];
    const keys = getLabTypeKeyByRoleMenuAttributes({ props });
    if (keys.length > 0) {
        whereLabType.push({ openBucket: true, name: "refLabType.key", operator: "in", type: "String", value: keys });
        whereLabType.push({
            closeBucket: true,
            logic: "AND",
            name: "refLabType.group",
            operator: "=",
            type: "String",
            value: "LAB_TYPE"
        });
    }
    const whereAll =  [...where, ...whereLabType];
    window.onSearch?.({ page, whereAll });
    return callSearch({
        criteria: {
            ...page,
            data: [],
            where: whereAll,
            children: [
                { name : "children", page: 0, size: 10000, select: ["**", "refLabFormula.**"]}
            ]
        }
    }).then(page => {
        state.page = { ...page, where }
    }).finally(() => setIsLoading(false));
};

export const state = reactive({
    columns: [],
    page: createPage({
        options: {
            select: ["**"],
            sort: [ { name: "seq", type: "asc" } ],
        }
    }),
    tableOptions: { search: { activeKey: "Search" } }
});

